import { useCallback, useState } from 'react'
import _ from 'lodash'

import {
  useDeleteViewDefaultRestrictionsByTypeMutation,
  useDeleteViewRestrictionsByTypeMutation,
  useLazyFetchDefaultRestrictionsByViewQuery,
  useLazyFetchRestrictionsByViewIdQuery,
  useUpdateDefaultRestrictionsMutation,
  useUpdateRestrictionsMutation,
} from '@redux/api/restriction.api'

import {
  ApiRestrictionData,
  RESTRICTION_TYPE,
  RESTRICTION_TYPE_TO_KEY,
  RestrictionData,
  RestrictionDTO,
} from '@types'

export type UseRestrictions = {
  restrictions: RestrictionData
  getRestrictions: (newViewId?: string | number) => Promise<RestrictionData | undefined>
  updateRestrictions: (
    restrictions: RestrictionDTO[],
    localRestrictions?: RestrictionDTO[],
    replaceSort?: boolean,
    replaceQSChoice?: boolean
  ) => Promise<unknown>
  removeRestrictions: (restrictionType: RESTRICTION_TYPE) => Promise<unknown>
  removeAllRestrictions: () => Promise<RestrictionData>
  isLoadingRestrictions: boolean
  isErrorRestrictions: boolean
  isFetchRestrictionsSuccess: boolean
  isUpdateRestrictionsSuccess: boolean
  isDeleteRestrictionsSuccess: boolean
  isFetchRestrictionsLoading: boolean
  isRestrictionsInitialized: boolean
}

export const useRestrictions = (
  viewId?: string | number,
  isDefault?: boolean,
  initialRestrictions?: ApiRestrictionData
): UseRestrictions => {
  const [restrictions, setRestrictions] = useState<RestrictionData>(
    !initialRestrictions
      ? {
          sort: [],
          order: [],
          fix: [],
          show: [],
          width: [],
          quickSearchPermission: [],
          quickSearchChoice: [],
        }
      : convertRestrictions(initialRestrictions)
  )

  const [currentViewId, setCurrentViewId] = useState<string | number | undefined>(viewId)

  const [isRestrictionsInitialized, setRestrictionsInitialized] = useState(
    Boolean(initialRestrictions)
  )

  const fetchRestrictionsMutation = isDefault
    ? useLazyFetchDefaultRestrictionsByViewQuery
    : useLazyFetchRestrictionsByViewIdQuery

  const updateRestrictionsMutation = isDefault
    ? useUpdateDefaultRestrictionsMutation
    : useUpdateRestrictionsMutation

  const deleteRestrictionsMutation = isDefault
    ? useDeleteViewDefaultRestrictionsByTypeMutation
    : useDeleteViewRestrictionsByTypeMutation

  const [
    fetchRestrictions,
    {
      isFetching: isFetchRestrictionsLoading,
      isSuccess: isFetchRestrictionsSuccess,
      isError: isFetchRestrictionsError,
    },
  ] = fetchRestrictionsMutation()

  // Для сброса пользовательских настроек
  const [fetchDefaultRestrictions] = useLazyFetchDefaultRestrictionsByViewQuery()

  const [
    postRestrictions,
    {
      isLoading: isUpdateRestrictionsLoading,
      isSuccess: isUpdateRestrictionsSuccess,
      isError: isUpdateRestrictionsError,
    },
  ] = updateRestrictionsMutation()

  const [
    deleteRestrictions,
    {
      isLoading: isDeleteRestrictionsLoading,
      isSuccess: isDeleteRestrictionsSuccess,
      isError: isDeleteRestrictionsError,
    },
  ] = deleteRestrictionsMutation()

  const getRestrictions = useCallback(
    async (newViewId?: string | number) => {
      setRestrictionsInitialized(false)
      const id = newViewId || currentViewId
      if (newViewId) {
        setCurrentViewId(newViewId)
      }
      if (!id) {
        setRestrictionsInitialized(true)
        setRestrictions({
          fix: [],
          show: [],
          sort: [],
          width: [],
          order: [],
          quickSearchPermission: [],
          quickSearchChoice: [],
        })

        return
      }
      try {
        const data = convertRestrictions(await fetchRestrictions({ viewId: id }).unwrap())
        setRestrictions({
          fix: data.fix || [],
          show: data.show || [],
          sort: data.sort || [],
          width: data.width || [],
          order: data.order || [],
          quickSearchChoice: data.quickSearchChoice || [],
          quickSearchPermission: data.quickSearchPermission || [],
        })

        setRestrictionsInitialized(true)

        return data
      } catch (err) {
        setRestrictionsInitialized(true)

        console.error('getRestrictions error', err)
        throw err
      }
    },
    [currentViewId]
  )

  const removeRestrictions = useCallback(
    async (restrictionType: RESTRICTION_TYPE) => {
      if (!currentViewId) return
      try {
        setRestrictions(prevState => {
          const restrictionKey = RESTRICTION_TYPE_TO_KEY[restrictionType]

          return {
            ...prevState,
            [restrictionKey]: [],
          }
        })

        return await deleteRestrictions({ viewId: currentViewId, restrictionType }).unwrap()
      } catch (err) {
        console.error('removeRestrictions error', err)
        throw err
      }
    },
    [currentViewId]
  )

  const removeAllRestrictions = useCallback(async () => {
    if (!currentViewId) return restrictions
    let newData
    try {
      if (!isDefault) {
        const data = convertRestrictions(
          await fetchDefaultRestrictions({ viewId: currentViewId }).unwrap()
        )
        setRestrictions(data)
        newData = data
      } else {
        const emptyRestrictions = {
          sort: [],
          order: [],
          fix: [],
          show: [],
          width: [],
          quickSearchPermission: [],
          quickSearchChoice: [],
        }
        setRestrictions(emptyRestrictions)
        newData = emptyRestrictions
      }
      await Promise.all([
        deleteRestrictions({ viewId: currentViewId, restrictionType: RESTRICTION_TYPE.SORT }),
        deleteRestrictions({ viewId: currentViewId, restrictionType: RESTRICTION_TYPE.SHOW }),
        deleteRestrictions({ viewId: currentViewId, restrictionType: RESTRICTION_TYPE.WIDTH }),
        deleteRestrictions({ viewId: currentViewId, restrictionType: RESTRICTION_TYPE.ORDER }),
        deleteRestrictions({ viewId: currentViewId, restrictionType: RESTRICTION_TYPE.FIX }),
        deleteRestrictions({
          viewId: currentViewId,
          restrictionType: RESTRICTION_TYPE.QUICK_SEARCH_CHOICE,
        }),
      ])

      return newData
    } catch (err) {
      console.error('removeAllRestrictions error', err)
      throw err
    }
  }, [currentViewId])

  const updateRestrictions = useCallback(
    async (
      changedRestrictions: RestrictionDTO[],
      localRestrictions?: RestrictionDTO[],
      replaceSort?: boolean,
      replaceQSChoice?: boolean
    ) => {
      if (!currentViewId || !changedRestrictions.length) return
      try {
        if (replaceSort) {
          await deleteRestrictions({
            viewId: currentViewId,
            restrictionType: RESTRICTION_TYPE.SORT,
          })
        }

        if (replaceQSChoice) {
          await deleteRestrictions({
            viewId: currentViewId,
            restrictionType: RESTRICTION_TYPE.QUICK_SEARCH_CHOICE,
          })
        }

        setRestrictions(prevState => {
          switch (changedRestrictions[0].restrictionType) {
            case RESTRICTION_TYPE.FIX:
              return {
                ...prevState,
                fix: _.unionBy(changedRestrictions, prevState.fix, 'fieldCode'),
              }
            case RESTRICTION_TYPE.ORDER:
              return {
                ...prevState,
                order: _.unionBy(
                  localRestrictions || changedRestrictions,
                  prevState.order,
                  'fieldCode'
                ),
              }
            case RESTRICTION_TYPE.SORT: {
              const newRestrictions = changedRestrictions.map(restriction => {
                if (typeof restriction.value === 'string' && restriction.value) {
                  const [columnToSort, sortDirection, priority] = restriction.value.split('//')

                  return {
                    ...restriction,
                    value: {
                      columnToSort,
                      sortDirection,
                      priority: Number(priority),
                    },
                  }
                }

                return restriction
              })

              return {
                ...prevState,
                sort: (replaceSort
                  ? newRestrictions
                  : _.unionBy(newRestrictions, prevState.sort, 'fieldCode')
                ).filter(({ value }) => value),
              }
            }
            case RESTRICTION_TYPE.WIDTH:
              return {
                ...prevState,
                width: _.unionBy(changedRestrictions, prevState.width, 'fieldCode'),
              }
            case RESTRICTION_TYPE.SHOW:
              return {
                ...prevState,
                show: _.unionBy(changedRestrictions, prevState.show, 'fieldCode'),
              }
            case RESTRICTION_TYPE.QUICK_SEARCH_PERMISSION:
              return {
                ...prevState,
                quickSearchPermission: _.unionBy(
                  changedRestrictions,
                  prevState.quickSearchPermission,
                  'fieldCode'
                ),
              }
            case RESTRICTION_TYPE.QUICK_SEARCH_CHOICE:
              return {
                ...prevState,
                quickSearchChoice: _.unionBy(
                  changedRestrictions,
                  prevState.quickSearchChoice,
                  'fieldCode'
                ),
              }
          }
        })

        return await postRestrictions(changedRestrictions).unwrap()
      } catch (err) {
        console.error('updateRestrictions error', err)
        throw err
      }
    },
    [currentViewId, restrictions]
  )

  const isLoadingRestrictions =
    isFetchRestrictionsLoading || isUpdateRestrictionsLoading || isDeleteRestrictionsLoading

  const isErrorRestrictions =
    isFetchRestrictionsError || isUpdateRestrictionsError || isDeleteRestrictionsError

  function convertRestrictions(restrictions: ApiRestrictionData): RestrictionData {
    return {
      sort: restrictions.sort || [],
      order: restrictions.order || [],
      fix: restrictions.fix || [],
      show: restrictions.show || [],
      width: restrictions.width || [],
      quickSearchChoice: restrictions.quick_search_choice || [],
      quickSearchPermission: restrictions.quick_search_permission || [],
    }
  }

  return {
    restrictions,
    getRestrictions,
    updateRestrictions,
    removeRestrictions,
    removeAllRestrictions,
    isFetchRestrictionsLoading,
    isLoadingRestrictions,
    isErrorRestrictions,
    isFetchRestrictionsSuccess,
    isUpdateRestrictionsSuccess,
    isDeleteRestrictionsSuccess,
    isRestrictionsInitialized,
  }
}
